import { useAuth } from '@/auth';
import { Button } from '@televet/kibble-ui/Button';
import { Heading } from '@televet/kibble-ui/Heading';
import { TextInput } from '@televet/kibble-ui/TextInput';
import { Alert } from '@televet/kibble-ui/Alert';
import { Tag } from '@televet/kibble-ui/Tag';
import { Flex } from '@televet/kibble-ui/chakra';

import {
  createFileRoute,
  useNavigate,
  useSearch,
} from '@tanstack/react-router';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';
import { useState } from 'react';
import { FirebaseError } from 'firebase/app';

interface LoginForm {
  email: string;
  password: string;
}

const loginSearchSchema = z.object({
  redirect: z.string().optional(),
  error: z.string().optional(),
});

export const Route = createFileRoute('/login')({
  component: RouteComponent,
  validateSearch: loginSearchSchema,
});

function getErrorMessage(error: unknown): string {
  if (error instanceof FirebaseError) {
    switch (error.code) {
      case 'auth/wrong-password':
        return 'Incorrect password. Please try again.';
      case 'auth/invalid-email':
        return 'Invalid email address format.';
      case 'auth/too-many-requests':
        return 'Too many failed login attempts. Please try again later.';
      case 'auth/invalid-credential':
        return 'Invalid email or password. Please try again.';
      case 'auth/user-not-found':
      case 'auth/user-disabled':
      default:
        return 'An error occurred during login. Please try again.';
    }
  }
  return 'An unexpected error occurred. Please try again.';
}

function RouteComponent() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const search = useSearch({ from: '/login' });
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { register, handleSubmit } = useForm<LoginForm>({
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit: SubmitHandler<LoginForm> = async ({ email, password }) => {
    setError(null);
    setIsSubmitting(true);
    try {
      await login({ email, password });
      navigate({ to: search.redirect ?? '/' });
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Flex
      className="Login__Container"
      h="100dvh"
      w="100%"
      bg="bg.subtle"
      justify="center"
      align="center"
    >
      <Flex
        className="Login__Content"
        direction="column"
        align="stretch"
        flex="1 1 auto"
        bg="bg"
        rounded="lg"
        p={16}
        maxW="520px"
        border="1px solid"
        borderColor="border.default"
        gap={10}
      >
        <Heading
          fontSize="3xl"
          display="flex"
          alignItems="center"
          textAlign="center"
          w="100%"
        >
          Welcome to Otto
          <Tag label="Admin" variant="success" />
        </Heading>

        {search.error === 'admin_access_required' && (
          <Alert
            status="error"
            rounded="md"
            description="Admin access is required to use this application. Please contact your administrator."
          />
        )}

        {error && (
          <Alert
            status="error"
            rounded="md"
            description={error}
          />
        )}

        <Flex
          as="form"
          className="Login__Form"
          direction="column"
          align="stretch"
          gap={4}
          onSubmit={handleSubmit(onSubmit)}
        >
          <TextInput
            label="Email"
            autoFocus
            {...register('email')}
          />

          <TextInput
            label="Password"
            type="password"
            {...register('password')}
          />

          <Button type="submit" mt={4} loading={isSubmitting}>
            Log In
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
}
